import * as _ from 'lodash';

import queryClient from '../../../ReactQuery/client';
import useToastedMutation from '../../../ReactQuery/useToastedMutation';
import { remove } from '../../../redux/api';
import { nuc } from '../../../utils';
import { getBaseDependencyFetchQueryKey } from '../dependencies/queryKey';

type DeleteLicenseParams = {
  dependencyRevisionLocator: string;
  revisionLicenseId: number;
};

const deleteLicense = ({
  dependencyRevisionLocator,
  revisionLicenseId,
}: DeleteLicenseParams) => {
  return remove(`/api/v2/revisions/${nuc(dependencyRevisionLocator)}/correction/license/${revisionLicenseId}`);
};

export const useDeleteLicense = ({
  dependencyRevisionLocator,
  revisionLicenseId,
}: DeleteLicenseParams) => {
  const queryKey = getBaseDependencyFetchQueryKey(dependencyRevisionLocator);

  return useToastedMutation({
    mutationFn: () => deleteLicense({ dependencyRevisionLocator, revisionLicenseId }),
    successToastMessage: 'License deleted successfully.',
    errorToastMessage: 'Failed to delete license.',
    onMutate: async () => {
      // Cancel any outgoing refetches to avoid optimistic update conflicts.
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(queryKey);

      // Optimistically update the cache by removing the license we're trying to delete.
      queryClient.setQueryData(queryKey, (old: unknown) => {
        if (!old || !_.isObject(old) || !('licenses' in old)) {
          return old;
        }

        const data = old as { licenses: Array<{ licenses: Array<{ revisionLicenseId: number }> }> };

        return {
          ...data,
          // oxlint-disable-next-line oxc/no-map-spread -- copy on purpose: `licenseGroup` is react-query cache data
          licenses: data.licenses.map((licenseGroup) => ({
            ...licenseGroup,
            licenses: licenseGroup.licenses.filter((license) =>
              license.revisionLicenseId !== revisionLicenseId,
            ),
          })),
        };
      });

      // Return a context object with the snapshot
      return { previousData };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (_err, _variables, context) => {
      // Revert the optimistic update on error
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
  });
};
